// src/pages/Home.jsx
import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchHomeBanners,
  fetchFeaturedProducts,
  fetchPromoLinks,
} from "../lib/home";
import { fetchFeaturedCategories, slugify } from "../lib/categories";
import { fetchActiveShelvesWithProducts } from "../lib/sponsored";
import { fetchActiveHeroGrids } from "../lib/heroGrids";
import { fetchActiveDealsSlides } from "../lib/dealsSlider";

import RailProducts from "../components/RailProducts";
import TrustStrip from "../components/TrustStrip";
import HowItWorks from "../components/HowItWorks";
import FaqBlock from "../components/FaqBlock";
import VendorsStrip from "../components/VendorsStrip";
import DealsSlider from "../components/DealsSlider";
import FeaturedVideos from "../components/FeaturedVideos";
import FullImageSlider from "../components/FullImageSlider";
import DealsCategoriesStrip from "../components/DealsCategoriesStrip";
import CategoryProductsStrip from "../components/CategoryProductsStrip";
import SponsoredProductsSection from "../components/home/SponsoredProductsSection";
import SponsoredShopsSection from "../components/home/SponsoredShopsSection";

/* =========================
   Helpers dates / nouveautés
========================= */
function parseFrenchDate(raw) {
  if (!raw) return null;
  const str = String(raw);

  const m = str.match(/^(\d{1,2}) (\w+) (\d{4}) à (\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;

  const day = parseInt(m[1], 10);
  const monthName = m[2].toLowerCase();
  const year = parseInt(m[3], 10);
  const hh = parseInt(m[4], 10);
  const mm = parseInt(m[5], 10);
  const ss = parseInt(m[6], 10);

  const months = {
    janvier: 0,
    fevrier: 1,
    février: 1,
    mars: 2,
    avril: 3,
    mai: 4,
    juin: 5,
    juillet: 6,
    aout: 7,
    août: 7,
    septembre: 8,
    octobre: 9,
    novembre: 10,
    decembre: 11,
    décembre: 11,
  };

  const month = months[monthName];
  if (month == null) return null;

  return Date.UTC(year, month, day, hh, mm, ss);
}

function startOfTodayMs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function getCreatedAtMs(p) {
  const v = p.CreateAt || p.createdAt || p.CreatedAt;
  if (!v) return null;

  if (v && typeof v.toDate === "function") {
    return v.toDate().getTime();
  }

  if (v instanceof Date) {
    return v.getTime();
  }

  if (typeof v === "number") {
    return v < 10_000_000_000 ? v * 1000 : v;
  }

  return parseFrenchDate(v);
}

function shuffleArray(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Home() {
  const [banners, setBanners] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [links, setLinks] = useState([]);
  const [topCats, setTopCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sponsored, setSponsored] = useState([]);
  const [heroGrids, setHeroGrids] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dealsSlides, setDealsSlides] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);

  const navigate = useNavigate();

  const sponsorTop = sponsored[0] || null;
  const sponsorMiddle = sponsored[1] || null;
  const sponsorBottom = sponsored[2] || null;

  useEffect(() => {
    (async () => {
      try {
        setLoadingDeals(true);
        const rows = await fetchActiveDealsSlides();
        setDealsSlides(rows);
      } finally {
        setLoadingDeals(false);
      }
    })();
  }, []);

  const loadHome = useCallback(async () => {
    setLoading(true);
    try {
      const [bn, fp, lk, cats, sp, hg] = await Promise.all([
        fetchHomeBanners(3),
        fetchFeaturedProducts({ pageSize: 24 }),
        fetchPromoLinks(8),
        fetchFeaturedCategories(8, 4),
        fetchActiveShelvesWithProducts(3),
        fetchActiveHeroGrids(1),
      ]);

      setBanners(bn || []);
      setFeatured(fp || []);
      setLinks(lk || []);
      setTopCats(cats || []);
      setSponsored(sp || []);
      setHeroGrids(hg || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHome();
  }, [loadHome]);

  const nouveautesSource = featured;

  const nouveautesTriees = useMemo(() => {
    if (!Array.isArray(nouveautesSource) || nouveautesSource.length === 0) {
      return [];
    }

    const todayStart = startOfTodayMs();
    const todayEnd = todayStart + 24 * 60 * 60 * 1000 - 1;
    const DAYS_BACK = 30;
    const limitFrom = todayStart - DAYS_BACK * 24 * 60 * 60 * 1000;

    const withDate = nouveautesSource
      .map((p) => ({
        ...p,
        _createdAtMs: getCreatedAtMs(p),
      }))
      .filter((p) => typeof p._createdAtMs === "number")
      .filter((p) => p._createdAtMs >= limitFrom && p._createdAtMs <= todayEnd);

    const pool = withDate.length > 0 ? withDate : nouveautesSource;
    return shuffleArray(pool).slice(0, 20);
  }, [nouveautesSource]);

  const offres = useMemo(() => featured.slice(10, 20), [featured]);
  const promosPrincipales = useMemo(() => links.slice(0, 4), [links]);
  const autresSites = useMemo(() => links.slice(4), [links]);

  const randomCats = useMemo(() => {
    if (!topCats || topCats.length <= 5) return topCats || [];

    const copy = [...topCats];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, 5);
  }, [topCats]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchTerm.trim();
    if (!q) return;
    navigate(`/catalog?q=${encodeURIComponent(q)}`);
  };

  const heroSlides = useMemo(() => {
    const list = Array.isArray(banners) ? banners : [];

    const actives = list.filter(
      (b) => b && (b.active === undefined || b.active === true)
    );

    if (!actives.length) {
      return [
        {
          id: "default",
          title: "Achetez sans compte, simplement.",
          subtitle:
            "Paiement Mobile Money ou à la livraison. Produits variés, vendeurs vérifiés.",
          imageUrl: null,
          href: "/catalog",
          backgroundColor: "#0b1226",
        },
      ];
    }

    return actives.slice(0, 3);
  }, [banners]);

  return (
    <div className="home-page">
      <FullImageSlider slides={heroSlides} />

      {/* Boutiques sponsorisées */}
      <SponsoredShopsSection
        title="Boutiques sponsorisées"
        subtitle="Explorez les boutiques mises en avant cette semaine"
        placementKey="homepage_featured_shops"
        maxPlacements={6}
      />

      <TrustStrip />

      <CategoryProductsStrip
        title="Household Essentials"
        categoryId="h3-NOd5nMqIN6hC1o0F43"
        limit={8}
        viewAllHref="/c/h3-NOd5nMqlN6hC1oOF43/household-essentials"
      />

      <DealsCategoriesStrip limit={15} />

      <CategoryProductsStrip
        title="Grocery"
        categoryId="WDL0PvUSLTdj2ssdtGbaB"
        limit={8}
        viewAllHref="/c/WDL0PvUSLTdj2ssdtGbaB/grocery"
      />

      {/* Hero grid */}
      {heroGrids[0] && (
        <section className="mb-4">
          <HeroGridBanner block={heroGrids[0]} />
        </section>
      )}

      {/* Produits sponsorisés */}
      <SponsoredProductsSection
        title="Produits sponsorisés"
        subtitle="Découvrez les produits mis en avant par nos vendeurs partenaires"
        placementKey="homepage_products"
        maxPlacements={8}
      />

      {/* Bloc sponsorisé interne top */}
      {sponsorTop && (
        <section className="mb-4">
          <SponsoredShelfBlock
            shelf={sponsorTop.shelf}
            products={sponsorTop.products}
          />
        </section>
      )}

      {/* Offres & services */}
      {promosPrincipales.length > 0 && (
        <section className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h3 className="m-0">Nos offres & services</h3>
          </div>

          <div className="row g-3">
            {promosPrincipales.map((p) => (
              <div className="col-12 col-md-6 col-lg-3" key={p.id}>
                <a
                  href={p.href || "#"}
                  target={p.href?.startsWith("http") ? "_blank" : "_self"}
                  rel="noreferrer"
                  className="home-promo-card card-modern text-decoration-none h-100"
                >
                  {p.logo && (
                    <div className="promo-image-wrapper">
                      <img src={p.logo} alt={p.title} loading="lazy" />
                    </div>
                  )}
                  <div className="p-3 d-flex flex-column h-100">
                    <div
                      className="fw-semibold mb-1"
                      style={{ color: "var(--text)" }}
                    >
                      {p.title || "Offre GoShop"}
                    </div>
                    {p.subtitle && (
                      <div className="small text-muted mb-2">{p.subtitle}</div>
                    )}
                    <span className="mt-auto small text-primary fw-semibold">
                      Découvrir &raquo;
                    </span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      <CategoryProductsStrip
        title="Beauty"
        categoryId="KArtEvX8emwLFq4l-xxbo"
        limit={8}
        viewAllHref="/c/h3-NOd5nMqlN6hC1oOF43/beauty"
      />

      {/* Nouveautés */}
      {nouveautesTriees.length > 0 && (
        <section className="mb-4">
          <RailProducts
            title="Nouveautés"
            subtitle="Les derniers produits ajoutés sur GoShop."
            products={nouveautesTriees}
          />
        </section>
      )}

      {/* Bloc sponsorisé interne milieu */}
      {sponsorMiddle && (
        <section className="mb-4">
          <SponsoredShelfBlock
            shelf={sponsorMiddle.shelf}
            products={sponsorMiddle.products}
          />
        </section>
      )}

      {/* Meilleures offres */}
      {offres.length > 0 && (
        <section className="mb-4">
          <RailProducts
            title="Meilleures offres"
            subtitle="Sélection de produits au meilleur rapport qualité / prix."
            products={offres}
          />
        </section>
      )}

      <VendorsStrip />

      <CategoryProductsStrip
        title="Baby"
        categoryId="8RaBQTlGREsAsPErMtZdP"
        limit={8}
        viewAllHref="/c/8RaBQTlGREsAsPErMtZdP/baby"
      />

      <HowItWorks />

      {/* Catégories */}
      {randomCats.length > 0 && (
        <section className="mb-5">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h3 className="m-0">Acheter par catégorie</h3>
            <Link to="/catalog" className="link-soft small">
              Voir tout
            </Link>
          </div>

          <div className="home-cat-rail">
            {randomCats.map((c) => (
              <Link
                key={c.id}
                to={`/c/${encodeURIComponent(c.id)}/${slugify(c.label)}`}
                className="home-cat-card text-decoration-none"
              >
                <div className="home-cat-media">
                  {c.imageUrl ? (
                    <img src={c.imageUrl} alt={c.label} />
                  ) : (
                    <div className="home-cat-media-fallback">
                      <span className="icon-letter">
                        {c.label?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="home-cat-content">
                  <div className="label">{c.label}</div>
                  <div className="sub">Voir les produits</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CategoryProductsStrip
        title="Personal Care"
        categoryId="Xm22XvKBDrJScGC7AlR7x"
        limit={8}
        viewAllHref="/c/Xm22XvKBDrJScGC7AlR7x/personal-care"
      />

      <FaqBlock />

      {/* Bloc sponsorisé interne bas */}
      {sponsorBottom && (
        <section className="mb-4">
          <SponsoredShelfBlock
            shelf={sponsorBottom.shelf}
            products={sponsorBottom.products}
          />
        </section>
      )}

      {/* Autres sites */}
      {autresSites.length > 0 && (
        <section className="mb-5">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h3 className="m-0">L&apos;écosystème GoShop</h3>
            <span className="text-muted small">
              Nos autres sites & services partenaires
            </span>
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {autresSites.map((l) => (
              <div className="col" key={l.id}>
                <a
                  href={l.href || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="card-modern d-flex align-items-center p-3 text-decoration-none h-100"
                >
                  {l.logo && (
                    <img
                      src={l.logo}
                      alt={l.title}
                      style={{ width: 48, height: 48, objectFit: "contain" }}
                      className="me-3"
                    />
                  )}
                  <div className="d-flex flex-column">
                    <div
                      className="fw-semibold"
                      style={{ color: "var(--text)" }}
                    >
                      {l.title}
                    </div>
                    {l.subtitle && (
                      <div className="small text-muted">{l.subtitle}</div>
                    )}
                  </div>
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      {!loadingDeals && dealsSlides.length > 0 && (
        <DealsSlider slides={dealsSlides} />
      )}

      <FeaturedVideos />

      <CategoryProductsStrip
        title="Health Personal Care"
        categoryId="mRkeLM0NVtwOzalqlKuy_"
        limit={8}
        viewAllHref="/c/mRkeLM0NVtwOzalqlKuy_/pharmacy-health-personal-care"
      />

      {loading && (
        <div className="text-center my-4 text-muted small">
          Chargement des contenus…
        </div>
      )}
    </div>
  );
}

/* =========================
   Sponsored shelf block
========================= */
function SponsoredShelfBlock({ shelf, products }) {
  const {
    title,
    subtitle,
    ctaLabel = "Shop now",
    ctaHref = "/catalog",
    bannerImageUrl,
    rightTitle = "Sélection sponsorisée",
    rightSubtitle = "",
    badgeLabel = "",
  } = shelf || {};

  return (
    <div className="card-modern p-3 sponsored-block">
      <div className="row g-3 align-items-stretch">
        <div className="col-12 col-lg-5">
          <div className="sponsored-hero h-100">
            {bannerImageUrl && (
              <div className="sponsored-hero-img">
                <img
                  src={bannerImageUrl}
                  alt={title || "Sponsor"}
                  loading="lazy"
                />
              </div>
            )}

            <div className="sponsored-hero-body">
              {title && (
                <p className="text-uppercase small text-muted mb-1">{title}</p>
              )}

              {subtitle && <h2 className="mb-3">{subtitle}</h2>}

              <Link
                to={ctaHref}
                className="btn btn-primary btn-sm sponsored-hero-cta"
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="d-flex align-items-baseline justify-content-between mb-2">
            <div>
              <h4 className="mb-0">{rightTitle}</h4>
              {rightSubtitle && (
                <div className="text-muted small">{rightSubtitle}</div>
              )}
            </div>

            <Link
              to="/catalog"
              className="link-soft small d-flex align-items-center gap-1"
            >
              <span>Voir tout</span>
              <span aria-hidden="true">›</span>
            </Link>
          </div>

          {badgeLabel && (
            <div className="mb-2">
              <span className="badge sponsored-badge">{badgeLabel}</span>
            </div>
          )}

          <RailProducts title="" subtitle="" products={products} />
        </div>
      </div>
    </div>
  );
}

/* =========================
   Hero grid banner
========================= */
function HeroGridBanner({ block }) {
  const {
    mainTitle,
    mainSubtitle,
    mainImageUrl,
    mainHref = "/catalog",
    mainCtaLabel = "Learn more",
    mainBgColor = "#006d38",

    tile1Title,
    tile1Subtitle,
    tile1ImageUrl,
    tile1Href = "/catalog",

    tile2Title,
    tile2Subtitle,
    tile2ImageUrl,
    tile2Href = "/catalog",

    tile3Title,
    tile3Subtitle,
    tile3ImageUrl,
    tile3Href = "/catalog",

    tile4Title,
    tile4Subtitle,
    tile4ImageUrl,
    tile4Href = "/catalog",
  } = block || {};

  const tiles = [
    {
      title: tile1Title,
      subtitle: tile1Subtitle,
      imageUrl: tile1ImageUrl,
      href: tile1Href,
    },
    {
      title: tile2Title,
      subtitle: tile2Subtitle,
      imageUrl: tile2ImageUrl,
      href: tile2Href,
    },
    {
      title: tile3Title,
      subtitle: tile3Subtitle,
      imageUrl: tile3ImageUrl,
      href: tile3Href,
    },
    {
      title: tile4Title,
      subtitle: tile4Subtitle,
      imageUrl: tile4ImageUrl,
      href: tile4Href,
    },
  ].filter((t) => t.title || t.imageUrl);

  return (
    <div className="card-modern p-3 hero-grid-banner">
      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <a
            href={mainHref}
            className="hero-grid-main text-decoration-none"
            style={{ background: mainBgColor }}
          >
            <div className="hero-grid-main-body">
              {mainTitle && (
                <div className="hero-grid-main-kicker">{mainTitle}</div>
              )}
              {mainSubtitle && (
                <h2 className="hero-grid-main-title">{mainSubtitle}</h2>
              )}
              <button type="button" className="hero-grid-main-cta">
                {mainCtaLabel}
              </button>
            </div>

            {mainImageUrl && (
              <div className="hero-grid-main-img">
                <img
                  src={mainImageUrl}
                  alt={mainSubtitle || mainTitle || "Bannière"}
                  loading="lazy"
                  style={{ objectFit: "contain" }}
                />
              </div>
            )}
          </a>
        </div>

        <div className="col-12 col-lg-7">
          <div className="row g-3">
            {tiles.map((t, idx) => (
              <div className="col-12 col-md-6" key={idx}>
                <a
                  href={t.href || "/catalog"}
                  className="hero-grid-tile text-decoration-none"
                >
                  {t.imageUrl && (
                    <div className="hero-grid-tile-img">
                      <img
                        src={t.imageUrl}
                        alt={t.title || "Bannière"}
                        loading="lazy"
                        style={{ objectFit: "contain" }}
                      />
                    </div>
                  )}

                  <div className="hero-grid-tile-body">
                    {t.title && (
                      <h3 className="hero-grid-tile-title">{t.title}</h3>
                    )}
                    {t.subtitle && (
                      <div className="hero-grid-tile-subtitle">
                        {t.subtitle}
                      </div>
                    )}
                    <span className="hero-grid-tile-link small">
                      Voir plus &raquo;
                    </span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}